import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Checkbox, Stack } from '@lyearn/ui';

import { useTaskBulkSelect } from '../../../../contexts/TaskBulkSelectContext';
import { SubtaskViewType } from '../../../../utils/getSubtasksViewItems';

type Props = {
  className?: string;
  actionItemId: string;
  groupByKey?: string;
  groupHoverClassName?: string;
  isRootTask?: boolean;
  subTaskView?: SubtaskViewType;
};

const TaskCheckbox = ({
  className,
  actionItemId,
  groupByKey,
  groupHoverClassName = '',
  isRootTask = false,
  subTaskView,
}: Props) => {
  const { t } = useTranslation();
  const {
    selectedGroups,
    includeActionItemIds,
    excludeActionItemIds,
    setIncludeActionItemIds,
    setExcludeActionItemIds,
  } = useTaskBulkSelect();

  const isGroupChecked =
    (isRootTask || subTaskView === SubtaskViewType.Separate) &&
    selectedGroups.includes(groupByKey ?? '');

  const checked = isGroupChecked
    ? !excludeActionItemIds.includes(actionItemId)
    : includeActionItemIds.includes(actionItemId);

  const onChange = useCallback(() => {
    if (isGroupChecked) {
      setExcludeActionItemIds((prev) =>
        prev.includes(actionItemId)
          ? prev.filter((id) => id !== actionItemId)
          : [...prev, actionItemId],
      );
    } else {
      setIncludeActionItemIds((prev) =>
        prev.includes(actionItemId)
          ? prev.filter((id) => id !== actionItemId)
          : [...prev, actionItemId],
      );
    }
  }, [actionItemId, isGroupChecked, setExcludeActionItemIds, setIncludeActionItemIds]);

  return (
    <Stack
      className={cn(className, { [groupHoverClassName]: !checked })}
      onClick={(e) => e.stopPropagation()}>
      {groupByKey ? (
        <Checkbox checked={checked} size="small" onChange={onChange} />
      ) : (
        <Box className="w-18 h-18 shrink-0" />
      )}
    </Stack>
  );
};

export default React.memo(TaskCheckbox);
